import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {IPasswordValidator} from "./password.validator";
import {AuthService} from "../../../auth/auth.service";

@Component({
  selector: 'em-change-password',
  templateUrl: './change-password.html',
  styleUrls: ["./change-password.scss"],
})
export class ChangePasswordComponent implements OnInit{

  @Input() userPasswordPattern: number;
  @Input() passwordMinLength: number;
  @Input() userName: string;

  @Output() onClose: EventEmitter<any> = new EventEmitter<any>();

  public formControls: FormGroup;
  public changePasswordErrorText: string;
  public changePasswordInProgress: boolean;

  public activeValidators: IPasswordValidator[];

  constructor(private authService: AuthService, private changeDetector: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.initFormValidators();
  }

  public isActiveValidator(passwordValidator: any): boolean {
    if (this.userPasswordPattern) {
      return (this.userPasswordPattern & passwordValidator.patternValue) !== 0;
    }
    return false;
  }

  public changePassword(){
    const userName = this.userName || this.getFormControlValue('userName');
    const oldPassword = this.getFormControlValue('oldPassword');
    const newPassword = this.getFormControlValue('password');

    this.changePasswordErrorText = '';

    if (newPassword === oldPassword) {
      this.changePasswordErrorText = "changePasswordSamePassword";
    } else if (userName.indexOf('\\') > -1) {
      this.changePasswordErrorText = "changePasswordOnlyLocalUser";
    } else {
      this.changePasswordInProgress = true;
      this.authService.changePassword(userName, oldPassword, newPassword)
        .subscribe((res) => {
          if (res.success === true) {
            this.onClose.emit();
          } else {
            this.changePasswordErrorText = res.exception;
            this.changePasswordInProgress = false;
            this.changeDetector.markForCheck();
          }
        });
    }
  }

  private initFormValidators() {
    const passwordValidators = [
      {
        name : "AtLeastOneNumber",
        message: "Fill at least one number",
        patternValue: 1,
        validator: this.patternValidator(/\d/, {hasNumber: true}),
        isValid: () => this.isPasswordControlValidForError('hasNumber')
      },
      {
        name : "AtLeastOneUpperCaseChar",
        message: "Fill at least one upper character",
        patternValue: 2,
        validator: this.patternValidator(/[A-Z]/, {hasCapitalCase: true}),
        isValid: () =>  this.isPasswordControlValidForError('hasCapitalCase')
      },
      {
        name : "AtLeastOneLowerCaseChar",
        message: "Fill at least one lower character",
        patternValue: 4,
        validator:  this.patternValidator(/[a-z]/, {hasLowerCase: true}),
        isValid: () =>  this.isPasswordControlValidForError('hasLowerCase')
      },
      {
        name : "AtLeastOneSpecialChar",
        message: "Fill at least one special character",
        patternValue: 8,
        validator: this.patternValidator(/[^\w\s]/, {hasSpecialCharacters: true}),
        isValid: () =>  this.isPasswordControlValidForError('hasSpecialCharacters')
      },
      {
        name : "PasswordMinimumLength",
        message: `Minimum length is ${this.passwordMinLength} characters`,
        patternValue: 16,
        validator: Validators.minLength(this.passwordMinLength),
        isValid: () =>   this.isPasswordControlValidForError('minlength'),
        translationParam: this.passwordMinLength.toString()
      },
      {
        name : "AtMostTwoConsecutiveChars",
        message: "Has at most 2 consecutive characters",
        patternValue: 32,
        validator: this.antiPatternValidator(/([^\w\s]|[a-zA-Z0-9])\1\1+/, {lessThanTwoConsecutiveChars: true}),
        isValid: () =>  this.isPasswordControlValidForError('lessThanTwoConsecutiveChars')
      }
    ];

    this.activeValidators = [];
    passwordValidators.forEach(item => {
      if (this.isActiveValidator(item)){
        this.activeValidators.push(item);
      }
    });
    const formValidators = this.activeValidators.map(validatorObj => validatorObj.validator);

    this.formControls = new FormGroup({
        ...(!this.userName && {userName: new FormControl('', {validators: [Validators.required]})}),
        oldPassword:  new FormControl('', {validators: [Validators.required]}),
        password: new FormControl('', {validators: [...formValidators,Validators.required]}),
        confirmPassword: new FormControl('', {validators: [Validators.required]})
      },
      {validators: [Validators.required, this.passwordMatchValidator({passwordsMismatch: true})]});
  }

  private passwordMatchValidator(error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const password: string = control.get('password').value; // get password from our password form control
      const confirmPassword: string = control.get('confirmPassword').value; // get password from our confirmPassword form control
      if (password !== confirmPassword) {
        control.get('confirmPassword').setErrors({passwordsMismatch: true});
        return error;
      } else {
        control.get('confirmPassword').setErrors(null);
        return null;
      }
    };
  }

  private patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return  regex.test(control.value) ? null : error;
    };
  }

  private antiPatternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      return regex.test(control.value) ? error : null;
    };
  }

  private isPasswordControlValidForError(error: string): boolean {
    return !this.formControls.controls['password'].hasError('required')
      &&  !this.formControls.controls['password'].hasError(error);
  }

  private getFormControlValue(controlName: string): string {
    return this.formControls.controls[controlName].value;
  }
}
